import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Heading, Stack, Text } from '@indeed/ifl-components';
import {
    ProctorOverridesPanel,
    SETUP_PROCTORS,
    proctorStateFromPrforceGroups,
    useProctorOverrides,
} from './proctorOverrides';
import { PRFORCE_GROUPS_SEARCH_PARAM, upsertSearchParams } from './rbsTestConfigParams';

// ── Entry points ──────────────────────────────────────────────────────────────

interface EntryPointOption {
    /** `from` query value; '' means no `from` param (direct / unknown). */
    value: string;
    label: string;
    /** Where the index redirect lands for this entry under progressive automation. */
    hint: string;
}

// getEntryPoint passes `?from=` through verbatim; AutoApplySetupIndexPage treats
// HOMEPAGE / JOB_SEARCH as "homepage" (review-before-submit) and known
// non-homepage entries (MyJobs/promo) as apply-for-me. Unknown entries default to
// review-before-submit unless the user is already in Apply For Me mode (IAFE-2081).
const ENTRY_POINT_OPTIONS: EntryPointOption[] = [
    {
        value: '',
        label: 'None / unknown',
        hint: 'review-before-submit (or apply-for-me if already in AFM mode)',
    },
    { value: 'HOMEPAGE', label: 'HOMEPAGE', hint: 'review-before-submit' },
    { value: 'JOB_SEARCH', label: 'JOB_SEARCH', hint: 'review-before-submit' },
    { value: 'MYJOBS', label: 'MYJOBS', hint: 'apply-for-me' },
];

const ENTRY_POINT_VALUES = ENTRY_POINT_OPTIONS.map((option) => option.value);
const DEFAULT_ENTRY_POINT = 'HOMEPAGE';
const FROM_SEARCH_PARAM = 'from';

const parseEntryPoint = (value: string | null): string =>
    value !== null && ENTRY_POINT_VALUES.includes(value) ? value : DEFAULT_ENTRY_POINT;

// ── Routes ──────────────────────────────────────────────────────────────────────

interface SetupRoute {
    /** Path appended to `/auto-apply-setup` ('' = the index/entry redirect). */
    path: string;
    label: string;
}

// Concrete, navigable routes under /auto-apply-setup (see App.tsx). Parameterized
// routes are expanded to their real values (review variants) or their base path.
const SETUP_ROUTES: SetupRoute[] = [
    { path: '', label: 'Index (entry redirect)' },
    { path: '/automation-level', label: 'Automation level' },
    { path: '/automation-level/edit', label: 'Automation level — edit' },
    { path: '/job-preferences', label: 'Job preferences' },
    { path: '/resume', label: 'Resume' },
    { path: '/resume/edit', label: 'Resume — edit' },
    { path: '/review', label: 'Review (base / legacy)' },
    { path: '/review/review-before-submit', label: 'Review — review-before-submit' },
    { path: '/review/apply-for-me', label: 'Review — apply-for-me' },
    { path: '/complete', label: 'Complete' },
    { path: '/past-answers', label: 'Past answers' },
    { path: '/terms', label: 'Terms' },
    { path: '/report-issue', label: 'Report issue' },
    { path: '/mid-setup-feedback', label: 'Mid-setup feedback' },
];

const buildSetupUrl = (
    origin: string,
    path: string,
    from: string,
    prforceGroups: string,
): string => {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (prforceGroups) params.set('prforceGroups', prforceGroups);
    const query = params.toString();
    return `${origin}/auto-apply-setup${path}${query ? `?${query}` : ''}`;
};

// ── Component ─────────────────────────────────────────────────────────────────

const TestSetupPage: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [entryPoint, setEntryPoint] = useState(() =>
        parseEntryPoint(searchParams.get(FROM_SEARCH_PARAM)),
    );

    // Forced proctor buckets appended to every generated setup link. Empty when
    // nothing is forced, in which case the `prforceGroups` param is dropped.
    // Hydrate once from the URL when `panelPrforceGroups` is present so a shared
    // link reproduces the same buckets. Pass SETUP_PROCTORS so invalid tokens
    // fall back to this page's defaults (banner 2, phone -1), not the RBS ones.
    const [initialProctors] = useState(() =>
        proctorStateFromPrforceGroups(
            searchParams.has(PRFORCE_GROUPS_SEARCH_PARAM)
                ? (searchParams.get(PRFORCE_GROUPS_SEARCH_PARAM) ?? '')
                : null,
            SETUP_PROCTORS,
        ),
    );
    const proctorOverrides = useProctorOverrides(SETUP_PROCTORS, initialProctors);
    const { prforceGroups } = proctorOverrides;

    const replaceSearchParams = (updates: Record<string, string | null>) => {
        setSearchParams((previous) => upsertSearchParams(previous, updates), { replace: true });
    };

    const handleEntryPointChange = (next: string) => {
        setEntryPoint(next);
        replaceSearchParams({ [FROM_SEARCH_PARAM]: next });
    };

    // Rendered client-only (see App route), so `window` is always defined here;
    // guarded anyway to stay safe if this is ever rendered during SSR.
    const origin = typeof window !== 'undefined' ? window.location.origin : '';

    return (
        <Box
            sx={{
                display: 'flex',
                gap: 8,
                alignItems: 'flex-start',
                flexWrap: 'wrap',
                maxInlineSize: '1100px',
                margin: '0 auto',
            }}
        >
            <Box sx={{ flex: '1 1 460px', minInlineSize: 0, padding: 8, fontFamily: 'mono' }}>
                <Heading level={1} sx={{ marginBlockEnd: 2 }}>
                    Setup Flow Testing
                </Heading>
                <Text variant="secondary" sx={{ marginBlockEnd: 8, display: 'block' }}>
                    Links to every supported <strong>/auto-apply-setup</strong> route. The selected
                    entry point (<code>from</code>) and forced proctor buckets in the side panel are
                    applied to every link below.
                    <br />
                    Entry point and Proctor overrides sync to the URL (
                    <code>{FROM_SEARCH_PARAM}</code>, <code>{PRFORCE_GROUPS_SEARCH_PARAM}</code>) so
                    you can bookmark or share a configuration. Supported Proctor fields are limited
                    to buckets from this app&apos;s specification; invalid URL tokens fall back to
                    the page default.
                    <br />
                    Only available in non-production environments.
                </Text>

                <Stack spacing={5} vertical>
                    {SETUP_ROUTES.map((route) => {
                        const url = buildSetupUrl(origin, route.path, entryPoint, prforceGroups);
                        const testId = `setup-route-${route.path.replace(/\//g, '-') || 'index'}`;
                        return (
                            <Box key={route.path || 'index'}>
                                <Text sx={{ display: 'block', fontSize: 'sm', fontWeight: '700' }}>
                                    {route.label}
                                </Text>
                                <Box
                                    sx={{
                                        marginBlockStart: 1,
                                        padding: 3,
                                        backgroundColor: 'background.weaker',
                                        border: '1px solid',
                                        borderColor: 'border.weak',
                                        borderRadius: 'md',
                                        fontFamily: 'mono',
                                        fontSize: 'sm',
                                        wordBreak: 'break-all',
                                    }}
                                >
                                    <a
                                        data-testid={testId}
                                        href={url}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        {url}
                                    </a>
                                </Box>
                            </Box>
                        );
                    })}
                </Stack>
            </Box>

            <Box sx={{ flex: '0 0 300px', minInlineSize: 0 }}>
                <Box
                    sx={{
                        marginBlockStart: 8,
                        padding: 4,
                        border: '1px solid',
                        borderColor: 'border.weak',
                        borderRadius: 'md',
                        backgroundColor: 'background.weaker',
                        fontFamily: 'mono',
                    }}
                >
                    <Heading level={3} sx={{ marginBlockEnd: 2 }}>
                        Entry point
                    </Heading>
                    <Text
                        variant="secondary"
                        sx={{ marginBlockEnd: 3, display: 'block', fontSize: 'sm' }}
                    >
                        Sets the <code>from</code> param on every link, which drives the index
                        redirect&apos;s review variant. The value syncs to this page&apos;s URL when
                        changed.
                    </Text>
                    <label
                        htmlFor="setup-entry-point"
                        style={{
                            display: 'block',
                            fontFamily: 'monospace',
                            fontSize: '13px',
                            fontWeight: 700,
                            marginBlockEnd: '4px',
                        }}
                    >
                        from
                    </label>
                    <select
                        id="setup-entry-point"
                        data-testid="setup-entry-point"
                        value={entryPoint}
                        onChange={(e) => handleEntryPointChange(e.target.value)}
                        style={{
                            width: '100%',
                            fontFamily: 'monospace',
                            fontSize: '13px',
                            padding: '4px',
                        }}
                    >
                        {ENTRY_POINT_OPTIONS.map((option) => (
                            <option key={option.value || 'none'} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                    <Text
                        variant="secondary"
                        sx={{ display: 'block', fontSize: 'xs', marginBlockStart: 2 }}
                    >
                        Index redirect →{' '}
                        {ENTRY_POINT_OPTIONS.find((option) => option.value === entryPoint)?.hint}
                    </Text>
                </Box>

                <ProctorOverridesPanel
                    controller={proctorOverrides}
                    onCommit={(nextPrforceGroups) => {
                        replaceSearchParams({
                            [PRFORCE_GROUPS_SEARCH_PARAM]: nextPrforceGroups,
                        });
                    }}
                    description={
                        <>
                            Forced buckets are appended to every link as <code>prforceGroups</code>.
                            Values sync to the page URL as{' '}
                            <code>{PRFORCE_GROUPS_SEARCH_PARAM}</code> when a bucket is selected so
                            a shared link restores this panel. Keep{' '}
                            <code>rnhp_auto_apply_banner_tst</code> at <code>2</code> to exercise
                            the entry-driven review variants. Set a proctor to <code>-1</code> to
                            leave it unforced.
                        </>
                    }
                />
            </Box>
        </Box>
    );
};

export default TestSetupPage;
